<template>
  <div class="order-accounting-docs mt-4">
    <h4 class="mb-3">{{ $t('accounting-documents') }}</h4>

    <div v-if="loading" class="text-center py-4">
      <b-spinner></b-spinner>
      <p class="text-muted mt-2">{{ $t('loading') }}...</p>
    </div>

    <div v-else-if="documents && documents.length > 0">
      <b-table
        :items="documents"
        :fields="columns"
        responsive
        hover
        small
        class="accounting-docs-table"
      >
        <template #cell(issueDate)="{ item }">
          <div>
            {{ item.issueDate | moment('L') }}
            <div v-if="item.cancellationDate" class="text-danger small">
              {{ $t('cancelled') }}: {{ item.cancellationDate | moment('L') }}
            </div>
          </div>
        </template>

        <template #cell(type)="{ item }">
          <b-badge :variant="getTypeVariant(item.type)">
            {{ $t(`accounting-document-type.${item.type}`) }}
          </b-badge>
        </template>

        <template #cell(status)="{ item }">
          <b-badge :variant="getStatusVariant(item.status)">
            {{ $t(`invoice-status.${item.status}`) }}
          </b-badge>
          <div v-if="item.cancellationStatus" class="text-danger small mt-1">
            {{ $t(`cancellation-status.${item.cancellationStatus}`) }}
          </div>
        </template>

        <template #cell(totalAmount)="{ item }">
          <span class="font-weight-bold">
            {{ (item.totalAmount + item.vatAmount) | currency }}
          </span>
          <div v-if="item.vatAmount > 0" class="text-muted small">
            {{ $t('net') }}: {{ item.totalAmount | currency }}
          </div>
        </template>

        <template #cell(download)="{ item }">
          <div>
            <!-- Main Document -->
            <div v-if="item.path">
              <a
                :href="`/documents-api/file${item.path}` | jwtToken"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <b-icon-download></b-icon-download>
                {{ item.number }}
              </a>
            </div>

            <!-- Cancellation Document -->
            <div v-if="item.cancellationPath" class="mt-2">
              <a
                :href="`/documents-api/file${item.cancellationPath}` | jwtToken"
                target="_blank"
                class="btn btn-sm btn-outline-danger"
              >
                <b-icon-download></b-icon-download>
                {{ item.cancellationNumber }}
                <span class="small">({{ $t('cancellation') }})</span>
              </a>
            </div>
          </div>
        </template>
      </b-table>
    </div>

    <div v-else class="text-center text-muted py-4">
      <p>{{ $t('no-accounting-documents') }}</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { BIconDownload } from 'bootstrap-vue';
import { RequestQueryBuilder } from '@dataui/crud-request';

export default {
  name: 'OrderAccountingDocs',
  components: {
    BIconDownload,
  },
  props: {
    jobId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      documents: [],
      loading: false,
    };
  },
  computed: {
    columns() {
      return [
        {
          label: this.$t('invoices.table.heading.issuing-date'),
          key: 'issueDate',
          sortable: true,
        },
        {
          label: this.$t('type'),
          key: 'type',
        },
        {
          label: this.$t('invoices.table.heading.status'),
          key: 'status',
        },
        {
          label: this.$t('invoices.table.heading.grand-total'),
          key: 'totalAmount',
          sortable: true,
        },
        {
          label: this.$t('invoices.table.heading.download'),
          key: 'download',
        },
      ];
    },
  },
  mounted() {
    console.log(
      '[OrderAccountingDocs] Component mounted with jobId:',
      this.jobId,
    );
    this.loadDocuments();
  },
  watch: {
    jobId: {
      handler(newVal, oldVal) {
        console.log(
          '[OrderAccountingDocs] jobId changed from',
          oldVal,
          'to',
          newVal,
        );
        this.loadDocuments();
      },
    },
  },
  methods: {
    buildSearch() {
      const search = {
        $and: [
          {
            $or: [
              {
                'jobs.apiId': {
                  $eq: this.jobId,
                },
              },
              {
                'jobs.sfid': {
                  $eq: this.jobId,
                },
              },
            ],
          },
        ],
      };

      return { search };
    },

    async loadDocuments() {
      console.log(
        '[OrderAccountingDocs] loadDocuments called with jobId:',
        this.jobId,
      );

      if (!this.jobId) {
        console.log('[OrderAccountingDocs] No jobId, skipping load');
        return;
      }

      this.loading = true;
      try {
        const { search } = this.buildSearch();
        console.log('[OrderAccountingDocs] Search object:', search);

        const query = {
          sort: {
            field: 'issueDate',
            order: 'DESC',
          },
        };

        const qb = RequestQueryBuilder.create(query);

        if (search) {
          qb.search(search);
        }

        const queryString = qb.query();

        const url = `/documents-api/accounting/documents?${queryString}`;

        const response = await axios.get(url);

        this.documents = response.data;
      } catch (error) {
        console.error(
          '[OrderAccountingDocs] Error loading accounting documents:',
          error,
        );
        console.error(
          '[OrderAccountingDocs] Error details:',
          error.response?.data,
        );

        this.documents = [];
      } finally {
        this.loading = false;
      }
    },

    getTypeVariant(type) {
      const variants = {
        invoice: 'info',
        credit_note: 'success',
      };
      return variants[type] || 'secondary';
    },

    getStatusVariant(status) {
      const variants = {
        draft: 'secondary',
        pending: 'warning',
        created: 'info',
        send_pending: 'warning',
        sent: 'primary',
        received: 'primary',
        paid: 'success',
        written_off: 'danger',
      };
      return variants[status] || 'secondary';
    },
  },
};
</script>

<style lang="scss" scoped>
.order-accounting-docs {
  .accounting-docs-table {
    font-size: 0.9rem;

    ::v-deep thead th {
      background-color: #f8f9fa;
      border-bottom: 2px solid #dee2e6;
    }
  }
}
</style>
